import * as THREE from 'three'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'
import { identificarLadoPeca, type HitVeiculo } from '../../dominio/didatico/inspecaoVeicular'

export interface OpcoesResolverHit {
  /** Raiz do veículo para cálculo de coordenadas locais */
  veiculoRoot?: THREE.Object3D | null
  /** ID padrão caso o container do veículo não declare explicitamente */
  veiculoIdPadrao?: string
}

/**
 * Determina se um Object3D deve ser ignorado na inspeção veicular
 * (ex: halos, rótulos HTML, gizmos, marcadores temporários).
 */
export function deveIgnorarObjeto(obj: THREE.Object3D): boolean {
  if (obj.userData?.ignorarHit) return true
  const nome = obj.name || ''
  if (
    nome.startsWith('halo:') ||
    nome.startsWith('rotulo:') ||
    nome.startsWith('marcador:') ||
    nome.startsWith('gizmo:') ||
    nome.startsWith('debug:')
  ) {
    return true
  }
  return false
}

/**
 * Extrai um HitVeiculo canônico a partir de uma interseção de raycast Three.js / R3F.
 *
 * Garante que:
 * 1. A normal é transformada para coordenadas de mundo pelo matrixWorld do mesh.
 * 2. O ponto local é convertido para o referencial da raiz do veículo.
 * 3. O ID da peça respeita o prefixo semântico de Peca (`selecionavel:`).
 * 4. Halos, rótulos e elementos utilitários são filtrados.
 */
export function resolverHitVeiculo(
  intersection: THREE.Intersection,
  opcoes?: OpcoesResolverHit,
): HitVeiculo | null {
  if (!intersection?.object) return null

  // Filtra objetos auxiliares
  if (deveIgnorarObjeto(intersection.object)) return null

  let pecaId: string | null = null
  let noPeca: THREE.Object3D | null = null
  let noVeiculo: THREE.Object3D | null = opcoes?.veiculoRoot ?? null
  let veiculoId: string | null =
    noVeiculo?.userData?.veiculoId ??
    (noVeiculo?.name?.startsWith('veiculo:')
      ? noVeiculo.name.slice('veiculo:'.length)
      : null)

  // Sobe a árvore procurando o nó da Peca e o container do veículo
  let atual: THREE.Object3D | null = intersection.object
  while (atual) {
    if (deveIgnorarObjeto(atual)) return null

    if (!pecaId) {
      if (atual.userData?.pecaId) {
        pecaId = atual.userData.pecaId
        noPeca = atual
      } else if (atual.name && atual.name.startsWith(PREFIXO_SELECIONAVEL)) {
        pecaId = atual.name.slice(PREFIXO_SELECIONAVEL.length)
        noPeca = atual
      }
    }

    if (!veiculoId) {
      if (atual.userData?.veiculoId) {
        veiculoId = atual.userData.veiculoId
        if (!noVeiculo) noVeiculo = atual
      } else if (atual.name && atual.name.startsWith('veiculo:')) {
        veiculoId = atual.name.slice('veiculo:'.length)
        if (!noVeiculo) noVeiculo = atual
      }
    } else if (!noVeiculo && (atual.userData?.veiculoId || atual.name?.startsWith('veiculo:'))) {
      noVeiculo = atual
    }

    atual = atual.parent
  }

  // Se não foi encontrada uma Peca semântica identificável, não gera HitVeiculo
  if (!pecaId) {
    return null
  }

  const veiculoIdFinal = veiculoId ?? opcoes?.veiculoIdPadrao ?? 'veiculoPrincipal'

  // Ponto de impacto em coordenadas de mundo
  const pontoMundo: [number, number, number] = [
    intersection.point.x,
    intersection.point.y,
    intersection.point.z,
  ]

  // Ponto de impacto em coordenadas locais do veículo
  const pontoLocalVetor = intersection.point.clone()
  if (noVeiculo) {
    noVeiculo.worldToLocal(pontoLocalVetor)
  } else if (noPeca?.parent) {
    noPeca.parent.worldToLocal(pontoLocalVetor)
  }
  const pontoLocal: [number, number, number] = [
    pontoLocalVetor.x,
    pontoLocalVetor.y,
    pontoLocalVetor.z,
  ]

  // Normal de superfície transformada para coordenadas de mundo
  let normalMundo: [number, number, number] | undefined
  if (intersection.face?.normal) {
    const normalVetor = intersection.face.normal.clone()
    normalVetor.transformDirection(intersection.object.matrixWorld).normalize()
    normalMundo = [normalVetor.x, normalVetor.y, normalVetor.z]
  }

  const lado = identificarLadoPeca(pecaId, pontoLocal[0])

  return {
    veiculoId: veiculoIdFinal,
    pecaId,
    lado,
    meshId: intersection.object.name || intersection.object.uuid,
    pontoLocal,
    pontoMundo,
    normalMundo,
    distancia: intersection.distance,
    faceIndex: intersection.faceIndex ?? undefined,
  }
}
